import React, { useState, useEffect } from 'react';
import Grid from '@mui/material/Grid2';

import { 
  Gift,
  Campaign 
} from "@dndModels";

import DandDAPI from "@dndUtilities/system_api";
import DataUtilities from '@dndUtilities/data_utilities';
import CommonUtilities from "@utilities/common_utilities";
import PageLinks from "@components/Navigation/PageLinks";
import LoadingBox from "@components/input/LoadingBox";
import FilterBox from "@dndComponents/filtering/FilterBox";
import DeleteButton from "@components/input/buttons/DeleteButton";
import CampaignSelectGift from "@dndComponents/campaign/CampaignSelectGift";

interface Props {
  campaign: Campaign;
  selected: { item: Gift, count: number }[];
  onSelect: (changed: { item: Gift, count: number }[]) => void;
}

const api = DandDAPI.getInstance();
const data_util = DataUtilities.getInstance();
const common_util = CommonUtilities.getInstance();

export default function CampaignGiftSelect({
  campaign,
  selected,
  onSelect
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [gifts, setGifts] = useState<Gift[]>([]);
  const [true_filter, setTrueFilter] = useState<any>({ search_string: [""], common: { "$in": [true] } });
  const [options, setOptions] = useState<Gift[]>([]);
  const [proficient, setProficient] = useState<boolean | null>(null);
  const [page_num, setPageNum] = useState(0);

  useEffect(() => {
    if (!fetched) {
      load();
    }
  });

  useEffect(() => {
    if (reloading) {
      setReloading(false);
      calc_use_list();
    }
    // eslint-disable-next-line
  }, [reloading]);

  const load = () => {
    setFetched(true);
    setLoading(true);
    data_util.loadData({ char: campaign }).then(() => {
      const new_items = data_util.gifts;
      setGifts(new_items);
      setLoading(false);
      setReloading(true);
    });
  }

  const updateFilter = (changed: any) => {
    const old_filter = {...true_filter};
    let is_changed = false;
    const the_filter = {...changed};
    if (the_filter.effect_type) {
      the_filter.the_effect_types = the_filter.effect_type;
      delete the_filter.effect_type;
    }
    if (the_filter.attack_type) {
      the_filter.the_attack_types = the_filter.attack_type;
      delete the_filter.attack_type;
    }
    the_filter.vehicle = { "$in": [undefined, false] };
    if (the_filter.proficient === undefined && proficient !== null) {
      setProficient(null);
      is_changed = true;
    } else if (proficient !== the_filter.proficient && (proficient !== null || the_filter.proficient !== undefined)) {
      setProficient(the_filter.proficient);
      is_changed = true;
    }
    delete the_filter.proficient;
    if (!is_changed) {
      is_changed = !common_util.recursive_compare_objects(old_filter, the_filter);
    }
    if (is_changed) {
      setPageNum(0);
      setTrueFilter(the_filter);
      setReloading(true);
    }
  }

  const calc_use_list = () => {
    const the_filter = {...true_filter};
    const filtered_items = api.filter("", gifts, the_filter) as Gift[];
    setOptions(filtered_items);
  }

  const renderConditions = () => {
    const page_size = 10;
    const sorted = options.sort((a, b) => { 
      return a.name.localeCompare(b.name); 
    });
    const page_count = Math.ceil(options.length / page_size);
    const filtered_and_paged = sorted.slice(page_size * page_num, page_size * (page_num + 1));
    return (
      <Grid
        container
        spacing={0}
        direction="row"
        size={{
          xs: 12
        }}>
        { filtered_and_paged.map(gift => {
          return renderGift(gift);
        })}
        <Grid
          size={{
            xs: 12
          }}>
          <PageLinks selected={page_num} page_count={page_count} onChange={setPageNum} />
        </Grid>
      </Grid>
    );
  }

  const renderGift = (gift: Gift) => {
    return (
      <Grid
        key={gift._id}
        className="padded"
        size={{
          xs: 12
        }}>
        { !reloading && 
          <CampaignSelectGift
            campaign={campaign}
            gift={gift}
            onSelect={(count: number) => {
              const new_selected = [...selected];
              new_selected.push({ item: gift, count });
              onSelect(new_selected);
            }}
          />
        }
      </Grid>
    );
  }

  const renderSelected = () => {
    if (selected.length > 0) {
      return (
        <>
          <Grid
            className="header centered"
            size={{
              xs: 12
            }}>
            Selected Conditions &nbsp;<DeleteButton label="Remove All" onClick={() => { onSelect([]); }} />
          </Grid>
          { selected.map(s => {
            return (
              <Grid
                key={s.item.id}
                size={{
                  xs: 12
                }}>
                <Grid container spacing={0} direction="row">
                  <Grid
                    size={{
                      xs: 9
                    }}>
                    { s.item.name }{ s.count > 1 ? ` x ${s.count}` : "" }
                  </Grid>
                  <Grid
                    size={{
                      xs: 3
                    }}>
                    <DeleteButton
                      onClick={() => {
                        onSelect(selected.filter(o => o.item.id !== s.item.id));
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
            );
          })}
        </>
      );
    }
    return null;
  }

  if (!fetched || loading) {
    return (<LoadingBox />);
  } else { 
    return (
      <Grid container spacing={0} direction="row">
        <Grid
          size={{
            xs: 12
          }}>
          <FilterBox 
            data_type="condition"
            quick_fields={["common","tags"]}
            flat
            dont_use_api
            all_objects={gifts}
            character_options={campaign.character_options}
            the_filter={true_filter}
            onChange={updateFilter}
          />
        </Grid>
        { renderConditions() }
        { renderSelected() }
      </Grid>
    );
  }
}
